import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { geography } from '../services/supabase';
import { Country, State, LocalGovernmentArea, Continent } from '../types/geography';

interface GeographyContextType {
  // All countries
  allCountries: Country[];
  
  // Filtered data based on selections
  countries: Country[];
  states: State[];
  lgas: LocalGovernmentArea[];
  
  // Selected values
  selectedContinent: string;
  selectedCountry: string;
  selectedState: string;
  selectedLga: string;
  
  // Setter functions
  setSelectedContinent: (continent: string) => void;
  setSelectedCountry: (country: string) => void;
  setSelectedState: (state: string) => void;
  setSelectedLga: (lga: string) => void;
  
  // Loading and error states
  loading: boolean;
  error: string | null;
}

const GeographyContext = createContext<GeographyContextType | undefined>(undefined);

export const useGeographyContext = () => {
  const context = useContext(GeographyContext);
  if (!context) {
    throw new Error('useGeographyContext must be used within GeographyProvider');
  }
  return context;
};

interface GeographyProviderProps {
  children: ReactNode;
  continents: Continent[];
}

export const GeographyProvider: React.FC<GeographyProviderProps> = ({ children, continents }) => {
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [lgas, setLgas] = useState<LocalGovernmentArea[]>([]);

  const [selectedContinent, setSelectedContinent] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedLga, setSelectedLga] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await geography.getCountries();
        if (fetchError) throw new Error('Failed to fetch countries');
        if (data) setAllCountries(data as Country[]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  // Update countries when continent changes
  useEffect(() => {
    const continent = continents.find(c => c.name === selectedContinent);
    if (continent && allCountries.length > 0) {
      const filtered = allCountries.filter(c => continent.countries.includes(c.name));
      setCountries(filtered);
      setSelectedCountry('');
      setStates([]);
      setLgas([]);
    } else {
      setCountries([]);
    }
  }, [selectedContinent, allCountries, continents]);

  // Fetch states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const fetchStates = async () => {
        const { data } = await geography.getStates(selectedCountry);
        const statesData = (data || []) as State[];
        setStates(statesData);
        setLgas([]);
        setSelectedState('');
        setSelectedLga('');
      };
      fetchStates();
    } else {
      setStates([]);
      setLgas([]);
    }
  }, [selectedCountry]);

  // Fetch LGAs when state changes
  useEffect(() => {
    if (selectedState) {
      const fetchLgas = async () => {
        const { data } = await geography.getLGAs(selectedState);
        const lgasData = (data || []) as LocalGovernmentArea[];
        setLgas(lgasData);
        setSelectedLga('');
      };
      fetchLgas();
    }
  }, [selectedState]);

  const value: GeographyContextType = {
    allCountries,
    countries,
    states,
    lgas,
    selectedContinent,
    selectedCountry,
    selectedState,
    selectedLga,
    setSelectedContinent,
    setSelectedCountry,
    setSelectedState,
    setSelectedLga,
    loading,
    error,
  };

  return (
    <GeographyContext.Provider value={value}>
      {children}
    </GeographyContext.Provider>
  );
};
